/**
 * avatars.ts — the shared preset-avatar catalog (Player Identity System V1).
 *
 * 50 first-party circular character avatars live at
 *   Public/Assets/game/avatars/avatar_{1..50}.png  (256×256, transparent corners)
 *
 * The backend keeps an identical key list (backend AvatarCatalog) and is the
 * authority on which key each player uses: humans carry their selected
 * preset (`avatar_key` on identity payloads, chosen in Settings), bots carry
 * a per-session stable assignment made server-side at seat creation. The
 * client NEVER picks an avatar for anyone — no seeded/random client-side
 * assignment — it only renders the authoritative key or falls back to the
 * display-name initial.
 *
 * Keys are IDs, never paths: every asset path is built HERE from a
 * catalog-validated key, so a server payload or stale value can never turn
 * into an arbitrary URL or filesystem path.
 *
 * Both repos pin the same SHA-256 checksum of the canonically ordered key
 * list in their specs so catalog drift is detected by tests (same
 * discipline as the country catalog).
 */

export const AVATAR_COUNT = 50;

/** Canonical catalog: avatar_1..avatar_50 in numeric order. */
export const AVATAR_KEYS: readonly string[] = Object.freeze(
  Array.from({ length: AVATAR_COUNT }, (_, i) => `avatar_${i + 1}`),
);

const AVATAR_KEY_SET: ReadonlySet<string> = new Set(AVATAR_KEYS);

/** Exact catalog membership — the only test that authorizes an asset path. */
export function isValidAvatarKey(key: unknown): key is string {
  return typeof key === 'string' && AVATAR_KEY_SET.has(key);
}

/**
 * Canonical form for comparison: trimmed lowercase, null when blank.
 * Does NOT imply validity — check isValidAvatarKey separately.
 */
export function normalizeAvatarKey(raw: string | null | undefined): string | null {
  const v = (raw ?? '').trim().toLowerCase();
  return v.length > 0 ? v : null;
}

/**
 * Public URL for a catalog-validated key; null for anything else (unknown,
 * malformed, traversal-like, or empty values can never become a path).
 */
export function avatarAssetPath(key: string | null | undefined): string | null {
  const normalized = normalizeAvatarKey(key);
  if (!isValidAvatarKey(normalized)) return null;
  return `/Assets/game/avatars/${normalized}.png`;
}

/**
 * Phaser texture-cache key for a catalog-validated avatar key; null for
 * anything else. Deterministic: the same avatar key always maps to the same
 * texture key on every client and every reconnect.
 */
export function avatarTextureKey(key: string | null | undefined): string | null {
  const normalized = normalizeAvatarKey(key);
  if (!isValidAvatarKey(normalized)) return null;
  return normalized; // the catalog key itself is the texture key
}

/**
 * Display-name initial for the avatar fallback (never used as the player's
 * displayed name — names always render in full). First code point of the
 * trimmed canonical display name, uppercased; 'P' (from the neutral
 * 'Player' fallback) when the name is blank/malformed. Code-point based so
 * non-BMP characters (emoji, CJK, etc.) don't split into surrogate halves.
 */
export function identityInitial(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  if (trimmed.length === 0) return 'P';
  const first = Array.from(trimmed)[0];
  return first.toLocaleUpperCase();
}
